import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useCookies } from "react-cookie";

function EventSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["auth_token"]);

  // Parse eventId from query string if provided
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("eventId");
  //console.log("Event ID:", eventId);
  // helper to turn "2025-04-10" → "04-10-2025"
const formatDate = (iso) => {
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${month}-${day}-${year}`;
};
  // Initial state for event and contact data
  const [eventData, setEventData] = useState({
    event: "",
    description: "",
    startDate: "",
    endDate: "",
    beginRegistrationDate: "",
    endRegistrationDate: "",
    expireDate: "",
    maxContacts: null,
  });
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    major: "",
    contactTypeId: 4,
    alumni: false,
  });
  const [addingNewContact, setAddingNewContact] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch event details from the backend
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setError("No event ID provided");
        setLoading(false);
        return;
      }

      try {
        //console.log(`Fetching event with ID: ${eventId}`);
        const response = await axios.get(`/api/events/${eventId}`, {
          headers: { Authorization: `Bearer ${cookies.auth_token}` },
        });
        //console.log("API Response:", response.data);

        if (response.data.success) {
          setEventData(response.data.event);
        } else {
          setError("Failed to load event data: " + (response.data.message || "Unknown error"));
        }
      } catch (err) {
        console.error("Error fetching event data:", err);
        if (err.response && err.response.status === 401) {
          navigate("/login"); // Redirect to login if token is invalid
        } else {
          setError(`Error loading event data: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, cookies.auth_token, navigate]);

  // Fetch contacts from the backend
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const token = cookies.auth_token;
        const res = await axios.get("/api/company/contacts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setContacts(res.data.contacts);
        } else {
          setError("Failed to load contacts");
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        // For development, create dummy contacts if API fails
        setContacts([
          { contactId: 1, name: "Jane Doe", email: "jane@example.com" },
          { contactId: 2, name: "John Smith", email: "john@example.com" },
        ]);
      }
    };
    fetchContacts();
  }, [cookies.auth_token]);

  // Load already-registered contacts for this event
  useEffect(() => {
    const fetchRegistered = async () => {
      if (!eventId) return;
      try {
        const res = await axios.get("/api/company/events", {
          headers: { Authorization: `Bearer ${cookies.auth_token}` },
        });
        if (res.data.success) {
          const evt = res.data.events.find(e => e.eventId === parseInt(eventId));
          if (evt && evt.contacts) {
            const ids = evt.contacts.map(c => c.contactId);
            setSelectedContactIds(ids);
          }
        }
      } catch (err) {
        console.error("Error loading registered contacts:", err);
      }
    };

    fetchRegistered();
  }, [eventId, cookies.auth_token]);

  // Toggle the selection of a contact
  const handleToggleContact = (contactId) => {
    setSelectedContactIds((prev) => {
      if (prev.includes(contactId)) {
        return prev.filter((id) => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  // Update new contact fields as the user types
  const handleNewContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewContact((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Add a new contact by sending a POST request to the backend
  const handleAddNewContact = async () => {
    try {
      //console.log("New contact data:", newContact);
      const token = cookies.auth_token;

      // Ensure all required fields are present
      if (!newContact.name || !newContact.email || !newContact.contactTypeId) {
        setError("Please fill in all required fields.");
        return;
      }

      // Validate character limits
      if (newContact.name.length > 100) {
        setError("Name cannot exceed 100 characters.");
        return;
      }
      if (newContact.email.length > 100) {
        setError("Email cannot exceed 100 characters.");
        return;
      }
      if (newContact.major && newContact.major.length > 100) {
        setError("Major cannot exceed 100 characters.");
        return;
      }
      // If major is not specified
      if (!newContact.major && newContact.alumni) {
        setError("Please specify Degree and Major");
        return;
      }
      // Ensure maximum 12 contacts
      if (contacts.length >= 12) {
        setError("Maximum limit of 12 contacts reached for this account.");
        return;
      }

      const response = await axios.post(
        "/api/company/contacts",
        newContact,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setContacts((prev) => [...prev, response.data.contact]);
        setSuccessMessage("New contact added successfully!");
        setNewContact({
          name: "",
          email: "",
          major: "",
          contactTypeId: 4,
          alumni: false,
        });
        setAddingNewContact(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError("Failed to add new contact: " + (response.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error adding new contact:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to add new contact");
    }
  };

  // Sign up the selected contacts by sending them to the backend
  const handleSignup = async () => {
    if (!eventId) {
      setError("Invalid event ID. Please try again.");
      return;
    }

    if (selectedContactIds.length === 0) {
      setError("No contacts selected for signup.");
      return;
    }

    try {
      const token = cookies.auth_token;
      const response = await axios.post(
        `/api/events/${eventId}/edit-signup`,
        { contactIds: selectedContactIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage("Contacts signed up successfully!");
        setTimeout(() => {
          setSuccessMessage("");
          navigate("/eventslist");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to sign up contacts.");
      }
    } catch (err) {
      console.error("Error signing up contacts:", err);
      setError(err.response?.data?.message || "Failed to sign up contacts");
    }
  };

  const cancelRegistration = async () => {
    if (!eventId) {
      setError("Invalid event ID. Please try again.");
      return;
    }

    try {
      const token = cookies.auth_token;
      const response = await axios.post(
        `/api/events/${eventId}/edit-signup`,
        { contactIds: [] },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage("Registration canceled successfully!");
        setTimeout(() => {
          setSuccessMessage("");
          navigate("/eventslist");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to cancel registration.");
      }
    } catch (err) {
      console.error("Error signing up contacts:", err);
      setError(err.response?.data?.message || "Failed to cancel registration");
    }
  };


  // Handle "Return to Home" button
  const handleReturnToHome = () => {
    navigate("/");
  };

  // Handle "Return to Events" button
  const handleReturnToEvents = () => {
    navigate("/eventslist");
  };

  // Handle dismissing error or success messages
  const handleDismissMessage = () => {
    setError(null);
    setSuccessMessage("");
  };

  return (
    <div className="Homepage min-h-screen bg-gradient-to-b from-[#7B2D2D] to-[#B56565] overflow-y-auto">
      <div className="login-container">
        <div className="Homepage-page w-full max-w-4xl py-4 mx-auto">
          {/* Event Details */}
          <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
            <h2 className="text-3xl normal-case no-underline">Event Signup:</h2>
            {loading ? (
              <p>Loading event details...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              <div className="mb-4">
                <h3 className="text-xl font-bold mb-2">
                  {eventData.event || "Event Title"}
                </h3>
                <p>{eventData.description}</p>
                <p className="text-sm">
                  Start: {formatDate(eventData.startDate) || "N/A"} | End: {formatDate(eventData.endDate) || "N/A"}
                </p>
                <p className="text-sm">
                  Registration: {formatDate(eventData.beginRegistrationDate) || "N/A"} – {formatDate(eventData.endRegistrationDate) || "N/A"}
                </p>
                <p className="text-sm">
                  Maximum Contacts Allowed: {eventData.maxContacts !== null ? eventData.maxContacts : "Not set"}
                </p>
              </div>
            )}
          </div>

          {/* Combined Manage Contacts Section */}
          <div className="bg-white rounded-2xl shadow-md p-6 text-black mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Manage Contacts:</h3>
              <button
                onClick={() => setAddingNewContact(!addingNewContact)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {addingNewContact ? "Cancel" : "Add New Contact"}
              </button>
            </div>

            {/* Existing Contacts List */}
            <div className="mb-4">
              <h4 className="text-lg font-semibold mb-2">Select Existing Contacts:</h4>
              {contacts.length === 0 ? (
                <p>No contacts found. Add a new one below.</p>
              ) : (
                <div className="flex flex-col gap-2 max-h-[20vh] overflow-y-auto">
                  {contacts.map((c) => (
                    <label key={c.contactId} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedContactIds.includes(c.contactId)}
                        onChange={() => handleToggleContact(c.contactId)}
                      />
                      <span>
                        {c.name} ({c.email})
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Contact Form */}
            {addingNewContact && (
              <div className="mt-4">
                <h4 className="text-lg font-semibold mb-2">Add New Contact:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newContact.name}
                      onChange={handleNewContactChange}
                      placeholder="Enter name here"
                      className="w-full px-2 py-1 border rounded"
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={newContact.email}
                      onChange={handleNewContactChange}
                      placeholder="Enter email here"
                      className="w-full px-2 py-1 border rounded"
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Degree, Major, & Year
                    </label>
                    <input
                      type="text"
                      name="major"
                      value={newContact.major}
                      onChange={handleNewContactChange}
                      placeholder="Enter degree, major, & year here if applicable"
                      disabled={!newContact.alumni}
                      required={newContact.alumni}
                      className={`w-full px-2 py-1 border rounded ${
                        !newContact.alumni ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      maxLength={100}
                      
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Type *
                    </label>
                    <input
                      type="text"
                      id="contactType"
                      name="contactType"
                      value="Other"
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                    />
                  </div>
                  <div className="flex items-center mt-2">
                    <label className="mr-2 text-sm font-medium text-gray-700">
                      Alumni
                    </label>
                    <input
                      type="checkbox"
                      name="alumni"
                      checked={newContact.alumni}
                      onChange={handleNewContactChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddNewContact}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Contact
                </button>
              </div>
            )}
          </div>

          {/* Final Signup Button */}
          <div className="bg-white rounded-2xl shadow-md p-6 text-black mb-4">
            <h3 className="text-xl font-bold mb-2">Manage Event Signup:</h3>
            {/* <p>Select your existing contacts or add new ones above:</p> */}
            <button
              onClick={handleSignup}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mx-2"
            >
              Sign Up Selected Contacts
            </button>
            <button
              onClick={cancelRegistration}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mx-2"
            >
              Cancel Registration
            </button>
          </div>

          {/* Display error or success messages */}
          {(error || successMessage) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
                {error && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-red-600">Error</p>
                    <p className="text-gray-700 mt-2">{error}</p>
                  </div>
                )}
                {successMessage && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-green-600">Success</p>
                    <p className="text-gray-700 mt-2">{successMessage}</p>
                  </div>
                )}
                <button
                  onClick={handleDismissMessage}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Return to Home and Return to Events Buttons */}
          <div className="flex justify-center gap-4 mt-4 mb-6">
            <button
              onClick={handleReturnToHome}
              className="bg-gray-300 text-black rounded-full px-6 py-3 shadow-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Return to Home
            </button>
            <button
              onClick={handleReturnToEvents}
              className="bg-gray-300 text-black rounded-full px-6 py-3 shadow-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Return to Events
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventSignup;
